import React, { useEffect, useState, useMemo } from 'react';
import {
    TrendingUp, Search, Code2, Users, Globe, BarChart3,
    DollarSign, ExternalLink, RefreshCw, BookOpen,
    AlertCircle, Tag, Compass, Layers, AtSign
} from 'lucide-react';
import { usernameMarketApi } from '@/utils/api';
import { useTranslation } from 'react-i18next';

interface ValuationData {
    success: boolean;
    cached?: boolean;
    username: string;
    valuation: {
        usd: number | null;
        priceTon: number | null;
        usdMin: number | null;
        usdMax: number | null;
        min?: number | null;
        max?: number | null;
        basis?: string;
        predict?: {
            neighbours: number;
            trainSize: number;
        } | null;
        refCount?: number;
    };
    status: string;
    statusLabel: string;
    meaning?: string | null;
    country?: {
        code: string | null;
        name: string | null;
        flag: string | null;
    };
    population?: number | null;
    link?: string;
    reportText?: string;
    signals?: {
        trends?: { interest?: number; peak?: number } | null;
        trendsBlocked?: boolean;
        search?: { results?: number; suggestions?: string[] } | null;
        github?: { users?: number; repos?: number } | null;
        forebears?: { forename?: number; surname?: number } | null;
        domain?: { available?: boolean; tlds?: string[] } | null;
        freq?: { peak?: number; total?: number; topLabel?: string; topFlag?: string } | null;
        wd?: {
            found?: boolean;
            label?: string;
            desc?: string;
            country?: { code?: string; name?: string; flag?: string };
            population?: number;
        } | null;
        name?: {
            found?: boolean;
            kind?: string;
            gender?: string;
            bestFlag?: string;
            usageRegions?: string[];
            countries?: Array<{ name: string; rank: number; flag?: string }>;
        } | null;
    };
    updatedAt?: string;
}

interface UsernameValuationCardProps {
    username?: string;
    className?: string;
}

export const UsernameValuationCard: React.FC<UsernameValuationCardProps> = ({ username, className = '' }) => {
    const { t, i18n } = useTranslation('store');
    const isRtl = i18n.language === 'ar' || i18n.language === 'fa';

    const [data, setData] = useState<ValuationData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const cleanUsername = useMemo(() => {
        if (!username) return '';
        return String(username).trim().replace(/^@/, '').replace(/^(umkt_|umkt_asset_|list_|card_)/i, '').toLowerCase();
    }, [username]);

    const fetchValuation = async (force = false) => {
        if (!cleanUsername || cleanUsername.length < 3) return;
        setLoading(true);
        setError(null);
        try {
            const res = await usernameMarketApi.valuation(cleanUsername, force);
            if (res.ok) {
                const json = await res.json();
                if (json.success) {
                    setData(json);
                } else {
                    setError(json.error || t('valuationError'));
                }
            } else {
                setError(t('valuationError'));
            }
        } catch (e: any) {
            console.error('Failed to load valuation', e);
            setError(t('valuationError'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (cleanUsername) {
            fetchValuation(false);
        }
    }, [cleanUsername]);

    if (!cleanUsername) return null;

    const fmtNum = (n: any) => (n == null || isNaN(n) ? '-' : Number(n).toLocaleString('en-US'));
    const fmtUsd = (n: any) => (n == null || isNaN(n) ? '-' : Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }));

    const getFragmentStatusText = (status: string, fallbackLabel?: string): string => {
        const s = String(status || '').toLowerCase();
        if (isRtl && fallbackLabel) return fallbackLabel;
        const key = `fragmentStatus_${s}`;
        const translated = (t as (k: string) => string)(key);
        if (typeof translated === 'string' && translated !== key) return translated;
        return isRtl ? (fallbackLabel || s) : (s ? s.charAt(0).toUpperCase() + s.slice(1) : String(t('noDataAvailable')));
    };

    return (
        <div
            dir={isRtl ? 'rtl' : 'ltr'}
            className={`rounded-[22px] bg-[#0c1017] border border-white/[0.08] p-4 text-white select-none ${className}`}
        >
            {/* Header: Title and refresh */}
            <div className="flex items-center justify-between pb-3 border-b border-white/[0.06]">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/[0.08] flex items-center justify-center text-white/70">
                        <Layers size={15} />
                    </div>
                    <div>
                        <div className="flex items-center gap-1.5">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-white/50">
                                {t('valuationTitle')}
                            </span>
                            {data?.cached && (
                                <span className="text-[8px] px-1.5 py-0.5 rounded bg-white/[0.06] border border-white/[0.08] text-white/60 font-medium">
                                    {t('valuationCached')}
                                </span>
                            )}
                        </div>
                        <h4 className="text-[13px] font-bold tracking-wide text-white/90 flex items-center gap-1.5 mt-0.5 font-mono">
                            <AtSign size={13} className="text-white/40" />
                            <span>{cleanUsername}</span>
                            {data?.country?.flag && (
                                <span className="text-xs ml-1" title={data.country.name || ''}>{data.country.flag}</span>
                            )}
                        </h4>
                    </div>
                </div>

                <button
                    onClick={() => fetchValuation(true)}
                    disabled={loading}
                    title={t('recalculateTooltip')}
                    className="p-2 rounded-lg bg-white/[0.03] hover:bg-white/[0.07] border border-white/[0.06] text-white/50 hover:text-white transition-colors active:scale-95 disabled:opacity-40"
                >
                    <RefreshCw size={13} className={loading ? 'animate-spin text-white' : ''} />
                </button>
            </div>

            {/* Loading State */}
            {loading && !data && (
                <div className="py-7 flex flex-col items-center justify-center gap-2.5 text-center">
                    <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    <span className="text-xs text-white/40 font-medium">{t('valuationLoading')}</span>
                </div>
            )}

            {/* Error State */}
            {error && !data && (
                <div className="py-5 flex items-center gap-2.5 text-white/60 text-xs">
                    <AlertCircle size={16} className="shrink-0 text-white/40" />
                    <span>{error}</span>
                </div>
            )}

            {/* Main Content */}
            {data && (
                <div className="space-y-3 pt-3">
                    {/* Primary Valuation Summary */}
                    <div className="p-3.5 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[11px] font-bold text-white/60 flex items-center gap-1.5">
                                <DollarSign size={14} className="text-white/50" />
                                {t('estimatedValuation')}
                            </span>
                            {data.valuation?.usd != null && (
                                <span className="text-xl font-bold font-mono text-white">
                                    ~${fmtUsd(data.valuation.usd)}
                                </span>
                            )}
                        </div>

                        {/* Range and TON details */}
                        <div className="flex flex-wrap items-center justify-between gap-2 text-[10.5px] text-white/70 pt-2 border-t border-white/[0.04]">
                            <div className="flex items-center gap-1.5">
                                <span className="text-white/40">{t('expectedRange')}:</span>
                                <span className="font-mono font-semibold text-white/90">
                                    ${fmtUsd(data.valuation?.usdMin)} – ${fmtUsd(data.valuation?.usdMax)}
                                </span>
                            </div>
                            {data.valuation?.priceTon != null && (
                                <div className="text-[10px] font-mono text-white/50">
                                    {fmtNum(data.valuation.priceTon)} TON
                                </div>
                            )}
                        </div>

                        {/* Model details note */}
                        {data.valuation?.predict && (
                            <div className="text-[9.5px] text-white/40 mt-2 font-medium">
                                {t('signalsAdvance', {
                                    n: data.valuation.predict.neighbours,
                                    total: data.valuation.predict.trainSize
                                })}
                            </div>
                        )}
                    </div>

                    {/* Fragment & Meaning Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {/* Fragment Status */}
                        <div className="p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.05] flex flex-col justify-between">
                            <div className="flex items-center gap-1.5 text-[10px] text-white/40 mb-1">
                                <Tag size={12} className="text-white/40" />
                                <span>{t('fragmentStatus')}</span>
                            </div>
                            <div className="text-xs font-semibold text-white/90">
                                {getFragmentStatusText(data.status, data.statusLabel)}
                            </div>
                        </div>

                        {/* Meaning */}
                        {data.meaning && (
                            <div className="p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.05] flex flex-col justify-between">
                                <div className="flex items-center gap-1.5 text-[10px] text-white/40 mb-1">
                                    <BookOpen size={12} className="text-white/40" />
                                    <span>{t('meaningTitle')}</span>
                                </div>
                                <div className="text-xs font-semibold text-white/90 truncate" title={data.meaning}>
                                    {data.meaning}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sources Grid (🔎 المصادر ٩) */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[10.5px] font-bold text-white/50 flex items-center gap-1.5">
                                <Layers size={13} className="text-white/40" />
                                {t('sourcesTitle')}
                            </span>
                            {data.link && (
                                <a
                                    href={data.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[10px] text-white/50 hover:text-white flex items-center gap-1 font-medium transition-colors"
                                >
                                    <span>{t('viewOnFragment')}</span>
                                    <ExternalLink size={10} />
                                </a>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-[10.5px]">
                            {/* Google Trends */}
                            <div className="p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.05] flex items-center gap-2">
                                <TrendingUp size={13} className="text-white/40 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-[9px] text-white/40">{t('sourceTrends')}</div>
                                    <div className="font-semibold truncate text-white/80">
                                        {data.signals?.trends?.interest != null ? `${data.signals.trends.interest}/100` : t('noDataAvailable')}
                                    </div>
                                </div>
                            </div>

                            {/* Google Search */}
                            <div className="p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.05] flex items-center gap-2">
                                <Search size={13} className="text-white/40 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-[9px] text-white/40">{t('sourceSearch')}</div>
                                    <div className="font-semibold truncate text-white/80">
                                        {data.signals?.search?.suggestions?.length
                                            ? t('searchSuggestions', { count: data.signals.search.suggestions.length })
                                            : (data.signals?.search?.results != null
                                                ? t('searchResults', { count: fmtNum(data.signals.search.results) })
                                                : t('noDataAvailable'))}
                                    </div>
                                </div>
                            </div>

                            {/* GitHub */}
                            <div className="p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.05] flex items-center gap-2">
                                <Code2 size={13} className="text-white/40 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-[9px] text-white/40">{t('sourceGithub')}</div>
                                    <div className="font-semibold truncate text-white/80 font-mono">
                                        {data.signals?.github?.users != null || data.signals?.github?.repos != null
                                            ? `${fmtNum(data.signals?.github?.users || 0)} user · ${fmtNum(data.signals?.github?.repos || 0)} repo`
                                            : t('noDataAvailable')}
                                    </div>
                                </div>
                            </div>

                            {/* Forebears */}
                            <div className="p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.05] flex items-center gap-2">
                                <Users size={13} className="text-white/40 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-[9px] text-white/40">{t('sourceForebears')}</div>
                                    <div className="font-semibold truncate text-white/80">
                                        {data.signals?.forebears?.forename != null || data.signals?.forebears?.surname != null
                                            ? `${data.signals.forebears.forename != null ? `${t('nameFirstName')} ${fmtNum(data.signals.forebears.forename)}` : ''} ${data.signals.forebears.surname != null ? `${t('nameSurname')} ${fmtNum(data.signals.forebears.surname)}` : ''}`
                                            : t('noDataAvailable')}
                                    </div>
                                </div>
                            </div>

                            {/* Wikidata / Geographic / Country */}
                            <div className="p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.05] flex items-center gap-2 col-span-2">
                                <Compass size={13} className="text-white/40 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-[9px] text-white/40">{t('sourceIdentity')}</div>
                                    <div className="font-semibold truncate text-white/90">
                                        {data.signals?.wd?.desc || data.signals?.wd?.label || data.meaning || t('notCommonName')}
                                        {data.signals?.wd?.country?.flag && ` ${data.signals.wd.country.flag}`}
                                        {data.population ? ` · ${t('populationCount', { count: fmtNum(data.population) })}` : ''}
                                    </div>
                                </div>
                            </div>

                            {/* Domains Available */}
                            <div className="p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.05] flex items-center gap-2 col-span-2">
                                <Globe size={13} className="text-white/40 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-[9px] text-white/40">{t('sourceDomains')}</div>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {data.signals?.domain?.tlds && data.signals.domain.tlds.length > 0 ? (
                                            data.signals.domain.tlds.map(tld => (
                                                <span key={tld} className="px-1.5 py-0.5 rounded bg-white/[0.06] border border-white/[0.08] text-white/80 font-mono text-[9px] font-semibold">
                                                    .{tld}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="text-white/40 text-[10px]">{t('allDomainsTaken')}</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Word Frequency */}
                            <div className="p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.05] flex items-center gap-2 col-span-2">
                                <BarChart3 size={13} className="text-white/40 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-[9px] text-white/40">{t('sourceFrequency')}</div>
                                    <div className="font-semibold truncate text-white/80">
                                        {data.signals?.freq?.peak
                                            ? t('wordPerBillion', { count: fmtNum(data.signals.freq.peak) })
                                            : t('wordRare')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
